// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { getLandingPage, getLandingPageV3, startTrial } from '@api/portalServices';
import type { GetLandingPageResponse, TrialStartOutcome } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { fromLandingPageV3, TRIAL_PRODUCT_KEY } from '@portal/containers/landing/landingView';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { featureSupported } from '../../featureFlagsUtils';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../errorHandler/errors';

// The start response carries the launch url before the landing page does, so it is kept here until
// the landing page reports its own.
const startedTrialKey = (tenantId: string | undefined, productKey: string) => [
  QueryKey.STARTED_TRIAL,
  tenantId,
  productKey,
];

export const useLandingPage = () => {
  const { workflowId, tenantId } = useSession();
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();
  const trialsEnabled = featureSupported('PRODUCT_TRIALS_ENABLED');

  return useQuery<GetLandingPageResponse, ApiError>({
    queryKey: [QueryKey.LANDING_PAGE, tenantId, workflowId, trialsEnabled],
    queryFn: async () => {
      try {
        if (trialsEnabled) {
          return fromLandingPageV3(
            await getLandingPageV3(workflowId ?? '', tenantId ?? ''),
            Date.now(),
            productKey => queryClient.getQueryData<string>(startedTrialKey(tenantId, productKey)),
          );
        }
        return await getLandingPage(workflowId ?? '', tenantId ?? '');
      } catch (e) {
        emitError(new AppError(e as ApiError));
        throw e;
      }
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
};

export const useStartTrial = () => {
  const { tenantId } = useSession();
  const { emitError } = useErrorContext();
  const queryClient = useQueryClient();

  return useMutation<TrialStartOutcome, ApiError>({
    mutationFn: () => startTrial({ productKey: TRIAL_PRODUCT_KEY }),
    retry: false,
    // Runs before onSettled, so the url is in place when the landing refetch reads it.
    onSuccess: outcome => {
      if (typeof outcome === 'string' || !outcome.trial.launchUrl) {
        return;
      }
      queryClient.setQueryData(
        startedTrialKey(tenantId, outcome.trial.productKey),
        outcome.trial.launchUrl,
      );
    },
    onError: e => {
      emitError(new AppError(e));
    },
    // Returned so the mutation stays pending until the refetch lands; otherwise the start button
    // re-enables while it still shows the stale offer.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [QueryKey.LANDING_PAGE] }),
  });
};
